import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useUser } from './WebSocketContext';

/**
 * ProtectedRoute Component
 * 
 * Wraps routes that require specific permissions.
 * Redirects unauthorized users to the read-only project dashboard.
 */
const ProtectedRoute = ({ children, requireAdmin = false, requireAdminEditor = false, allowReadOnly = false }) => {
    const { isReadOnly, isAdmin, permissions } = useUser();
    const isAdminOrEditor = permissions === 'admin' || permissions === 'editor';
    const canBypassAdminEditorCheck = allowReadOnly && isReadOnly;

    // If route requires admin and user is not admin, show access denied
    if (requireAdmin && !isAdmin) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                sx={{ backgroundColor: '#f5f5f5', p: 3 }}
            >
                <Typography variant="h5" sx={{ mb: 2, color: '#d32f2f', fontWeight: 600 }}>
                    Access Denied
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', textAlign: 'center', maxWidth: 500 }}>
                    You do not have permission to access this page. This page requires administrator privileges.
                </Typography>
            </Box>
        );
    }

    // Redirect before rendering children to avoid flashing protected screens.
    if (requireAdminEditor && !isAdminOrEditor && !canBypassAdminEditorCheck) {
        return <Navigate to="/project-dashboard" replace />;
    }

    // If user is read-only (viewer), redirect immediately instead of rendering children first.
    if (isReadOnly && !allowReadOnly) {
        return <Navigate to="/project-dashboard" replace />;
    }

    // User has appropriate permissions, render the protected content
    return children;
};

export default ProtectedRoute;
